import React from 'react';
import { updateMySettings, updateMyTheme } from '../api';
import { NavLink } from 'react-router-dom';
import { getActiveTheme, isLocalOnlyThemeName } from '../ui/tokens';

export default function Sidebar({ user, darkMode, setDarkMode, theme, onThemeChange }) {
  const role = user?.role;
  const canSeeRed2 = String(user?.email || '').trim().toLowerCase() === 'admin@example.com';
  const localOnly = isLocalOnlyThemeName(theme);
  const tokenStyled = localOnly || theme === 'auroraExecutive' || theme === 'auroraExecutiveLight' || theme === 'redLightPremium' || theme === 'red2';
  const activeTheme = tokenStyled ? getActiveTheme() : null;
  const isSolidRedFocus = theme === 'lightRedFocus';
  const isRed2 = theme === 'red2';
  const isRedPremium = theme === 'redLightPremium';
  const isJuniorManager = role === 'junior_manager';
  const isManagerLike = role === 'manager' || role === 'admin' || isJuniorManager;
  const canSeeFeedback = role === 'manager' || role === 'admin';
  const canSeeVacation = role === 'manager' || role === 'admin';

  const warmFocusFontStyle = isSolidRedFocus
    ? {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
      }
    : null;

  const navLinkClass = ({ isActive }) => {
    if (tokenStyled) {
      return `block px-2 py-2 rounded transition-colors duration-150 ${isActive ? 'font-semibold' : ''}`;
    }
    return `block px-2 py-2 rounded transition-colors duration-150 dark:text-white ${
      isActive ? 'bg-gray-300 dark:bg-gray-700 font-semibold' : 'hover:bg-gray-300 dark:hover:bg-gray-700'
    }`;
  };

  const navLinkStyle = ({ isActive }) => {
    if (!tokenStyled) return undefined;

    if (isSolidRedFocus) {
      return {
        color: '#ffffff',
        background: isActive ? 'rgba(255,255,255,0.18)' : 'transparent',
        border: '1px solid transparent',
      };
    }

    if (isRed2) {
      return {
        color: activeTheme?.primary || '#e11d48',
        background: isActive ? (activeTheme?.primarySoft ? `rgba(225,29,72,0.10)` : 'rgba(225,29,72,0.10)') : 'transparent',
        borderLeft: `3px solid ${isActive ? (activeTheme?.primary || '#e11d48') : 'transparent'}`,
        borderRadius: 10,
        fontWeight: isActive ? 700 : 500,
      };
    }

    if (theme === 'auroraExecutiveLight') {
      return {
        color: activeTheme?.text || '#1F2937',
        background: isActive ? 'rgba(80,99,168,0.08)' : 'transparent',
        borderLeft: `3px solid ${isActive ? (activeTheme?.primary || '#5063A8') : 'transparent'}`,
        borderRadius: 10,
        fontWeight: isActive ? 600 : 400,
      };
    }

    if (theme === 'redLightPremium') {
      return {
        color: activeTheme?.text || '#1E1E1E',
        background: isActive ? (activeTheme?.primarySoft || 'rgba(215,38,56,0.08)') : 'transparent',
        borderLeft: `3px solid ${isActive ? (activeTheme?.primary || '#D72638') : 'transparent'}`,
        borderRadius: 10,
        fontWeight: isActive ? 600 : 400,
      };
    }

    if (theme === 'auroraExecutive') {
      return {
        color: isActive ? (activeTheme?.text || '#F0F3F8') : (activeTheme?.mutedText || '#A6B3C6'),
        background: isActive ? (activeTheme?.selected || activeTheme?.primary || '#731F55') : 'transparent',
        border: '1px solid transparent',
      };
    }

    return {
      color: isActive ? (activeTheme?.primary || '#0f172a') : (activeTheme?.text || '#0f172a'),
      background: isActive ? (activeTheme?.primarySoft || 'rgba(255,255,255,0.72)') : 'transparent',
      border: '1px solid transparent',
    };
  };

  const onNavHoverIn = (e) => {
    if (!tokenStyled) return;
    const isActive = !!e.currentTarget.getAttribute('aria-current');
    if (isActive) return;

    if (isSolidRedFocus) {
      e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
      return;
    }

    if (isRed2) {
      e.currentTarget.style.background = 'rgba(225,29,72,0.06)';
      return;
    }

    if (theme === 'auroraExecutive') {
      e.currentTarget.style.background = (activeTheme?.surfaceElevated || activeTheme?.surface2 || 'transparent');
      return;
    }

    if (theme === 'auroraExecutiveLight') {
      e.currentTarget.style.background = 'rgba(80,99,168,0.05)';
      return;
    }

    if (theme === 'redLightPremium') {
      e.currentTarget.style.background = 'rgba(215,38,56,0.05)';
      return;
    }

    e.currentTarget.style.background = (activeTheme?.surface2 || 'transparent');
  };

  const onNavHoverOut = (e) => {
    if (!tokenStyled) return;
    const isActive = !!e.currentTarget.getAttribute('aria-current');
    if (!isActive) e.currentTarget.style.background = 'transparent';
  };

  const links = isManagerLike
    ? [
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Workforce Overview', path: '/control-panel' },
        ...(isJuniorManager ? [{ name: 'My Work', path: '/my-work' }] : []),
        { name: 'Reports', path: '/reports' },
        ...(canSeeVacation ? [{ name: 'Vacations', path: '/vacation' }] : []),
        { name: 'Settings', path: '/settings' },
      ]
    : [
        { name: 'My Work', path: '/my-work' },
        { name: 'Settings', path: '/settings' },
      ];

  return (
    <div
      className={`w-64 p-4 ${tokenStyled ? '' : 'bg-gray-200 dark:bg-gray-800'}`}
      style={
        tokenStyled
          ? {
              background: theme === 'auroraExecutive'
                ? (activeTheme?.surface2 || '#111C2F')
                : (theme === 'auroraExecutiveLight'
                  ? (activeTheme?.surface || '#FFFFFF')
                  : (theme === 'redLightPremium'
                    ? (activeTheme?.surface || '#FFFFFF')
                    : (isRed2 ? '#FFFFFF' : (isSolidRedFocus ? (activeTheme?.primary || '#e11d48') : 'rgba(255,255,255,0.72)')))),
              borderRight: `1px solid ${activeTheme?.border || '#E5E9F0'}`,
              backdropFilter: theme === 'auroraExecutiveLight' || theme === 'auroraExecutive' || theme === 'redLightPremium' ? 'none' : 'blur(12px)',
              WebkitBackdropFilter: theme === 'auroraExecutiveLight' || theme === 'auroraExecutive' || theme === 'redLightPremium' ? 'none' : 'blur(12px)',
              color: theme === 'auroraExecutive'
                ? (activeTheme?.text || '#F0F3F8')
                : (theme === 'auroraExecutiveLight'
                  ? (activeTheme?.text || '#1F2937')
                  : (theme === 'redLightPremium'
                    ? (activeTheme?.text || '#1E1E1E')
                    : (isRed2 ? (activeTheme?.text || '#0f172a') : (isSolidRedFocus ? '#ffffff' : undefined)))),
              ...(warmFocusFontStyle || {}),
            }
          : undefined
      }
    >
      <ul className="space-y-2">
        {links.map(link => (
          <li key={link.name}>
            <NavLink
              to={link.path}
              className={navLinkClass}
              style={navLinkStyle}
              onMouseEnter={onNavHoverIn}
              onMouseLeave={onNavHoverOut}
              end
            >
              {link.name}
            </NavLink>
          </li>
        ))}
      </ul>
      <div className="mt-6">
        <label
          className={`block text-sm mb-1 ${isSolidRedFocus ? 'text-white' : 'dark:text-white'}`}
          style={isRedPremium ? { color: activeTheme?.mutedText || '#6B7280' } : undefined}
        >
          Theme
        </label>
        <select
          value={theme}
          onChange={async (e) => {
            const t = e.target.value;
            if (onThemeChange) await onThemeChange(t);
            else setDarkMode(t === 'auroraExecutive');
          }}
          className={`w-full px-2 py-2 rounded transition-colors duration-150 ${
            isRedPremium
              ? 'bg-white text-gray-900 border focus:outline-none focus:ring-2 focus:ring-[rgba(215,38,56,0.25)]'
              : 'dark:text-white '
          }${
            isRedPremium
              ? ''
              : (isSolidRedFocus || theme === 'auroraExecutive'
                ? 'text-white hover:brightness-95'
                : 'bg-indigo-500 text-white hover:bg-indigo-600 dark:bg-gray-700')
          }`}
          style={
            isRedPremium
              ? {
                  background: activeTheme?.surface || '#FFFFFF',
                  border: `1px solid ${activeTheme?.border || '#E6E8EC'}`,
                }
              : (isSolidRedFocus || theme === 'auroraExecutive'
              ? {
                  background: theme === 'auroraExecutive' ? (activeTheme?.primary || '#5063A8') : (activeTheme?.primary || '#fb7185'),
                  border: `1px solid ${activeTheme?.border || '#cbd5e1'}`,
                }
              : undefined)
          }
        >
          <option value="auroraExecutive">Dark</option>
          <option value="auroraExecutiveLight">Light</option>
          <option value="redLightPremium">Light Red</option>
          <option value="lightRedFocus">Red Prime</option>
          {canSeeRed2 && <option value="red2">Red 2</option>}
        </select>
      </div>

    {canSeeFeedback && (
      <div className="mt-6">
      <NavLink
        to="/feedback"
        className={navLinkClass}
        style={navLinkStyle}
        onMouseEnter={onNavHoverIn}
        onMouseLeave={onNavHoverOut}
        end
      >
        Comments & Feedback
      </NavLink>
      </div>
    )}
    </div>
  );
}
